import type { ReactNode } from 'react';
import clsx from 'clsx';
import { AppText } from '@/components/common';

export interface AppBottomSheetProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  className?: string;
}

/**
 * Reusable bottom sheet.
 * Slides up from the bottom on mobile, centers as a modal on larger screens.
 */
export default function AppBottomSheet({ open, onClose, title, children, className }: AppBottomSheetProps) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center sm:items-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[1px]" onClick={onClose} />
      <div
        className={clsx(
          'relative z-10 grid w-full max-w-md gap-5 rounded-t-3xl bg-white p-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] shadow-2xl',
          'sm:rounded-3xl',
          'animate-[appBottomSheetSlideUp_0.22s_ease-out]',
          className,
        )}
      >
        <div className="mx-auto -mt-1 h-1 w-10 rounded-full bg-[#E2E8F0] sm:hidden" />
        <div className="flex items-center justify-between">
          <AppText variant="h5">{title}</AppText>
          <button
            className="grid size-8 place-items-center rounded-full text-[#94A3B8] hover:bg-[#F1F5F9]"
            type="button"
            onClick={onClose}
          >
            ✕
          </button>
        </div>
        {children}
      </div>
      <style>{`@keyframes appBottomSheetSlideUp { from { transform: translateY(24px); opacity: 0 } to { transform: translateY(0); opacity: 1 } }`}</style>
    </div>
  );
}